import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Lock, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { api, isApiError, unwrap } from "@/api/client";
import { useLogout } from "@/auth/session";
import { useSession } from "@/auth/session-context";
import { useDocumentTitle } from "@/components/page";
import { LoadingState } from "@/components/states";
import { Button } from "@punto-firma/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@punto-firma/ui/components/card";

import { CenteredScreen } from "./centered-screen";
import { leaveTo } from "./leave-to";

/**
 * Autorizar la app de Mac (OAuth 2.0 + PKCE desde ASWebAuthenticationSession).
 * `GET /v1/auth/authorize` (api) valida la app y su `redirect_uri`, elige la
 * organización y redirige aquí con `?request=<id>`: se lee la petición
 * (`GET /v1/auth/authorize-requests/{id}`) y se aprueba o se deniega; api
 * devuelve la URL de vuelta a la app (`redirectUrl`), a la que se navega.
 */
export const ConnectMacPage = () => {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("connectMac.title"));
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request") ?? "";
  const { me } = useSession();
  const logout = useLogout();
  const [outcome, setOutcome] = useState<"connected" | "cancelled" | null>(
    null,
  );

  const request = useQuery({
    queryKey: ["authorize-request", requestId],
    queryFn: () =>
      unwrap(
        api.GET("/v1/auth/authorize-requests/{requestId}", {
          params: { path: { requestId } },
        }),
      ),
    enabled: requestId !== "",
    retry: false,
  });

  const decide = useMutation({
    mutationFn: (decision: "approve" | "deny") =>
      unwrap(
        decision === "approve"
          ? api.POST("/v1/auth/authorize-requests/{requestId}/approve", {
              params: { path: { requestId } },
            })
          : api.POST("/v1/auth/authorize-requests/{requestId}/deny", {
              params: { path: { requestId } },
            }),
      ),
    onSuccess: (decision, kind) => {
      setOutcome(kind === "approve" ? "connected" : "cancelled");
      leaveTo(decision.redirectUrl);
    },
  });

  if (requestId === "" || request.isError) {
    const expired = isApiError(request.error) && request.error.status === 404;
    return (
      <CenteredScreen>
        <Card>
          <CardHeader>
            <Lock className="size-6 text-muted-foreground" aria-hidden />
            <CardTitle>
              {expired ? t("connectMac.expired") : t("connectMac.invalid")}
            </CardTitle>
            <CardDescription>
              {t("connectMac.invalidDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </CenteredScreen>
    );
  }

  if (!request.data) {
    return (
      <CenteredScreen>
        <Card>
          <CardContent className="p-6">
            <LoadingState rows={3} />
          </CardContent>
        </Card>
      </CenteredScreen>
    );
  }

  if (outcome) {
    const connected = outcome === "connected";
    const Icon = connected ? CheckCircle2 : XCircle;
    return (
      <CenteredScreen>
        <Card role="status">
          <CardHeader>
            <Icon
              className={
                connected
                  ? "size-6 text-success"
                  : "size-6 text-muted-foreground"
              }
              aria-hidden
            />
            <CardTitle>
              {connected ? t("connectMac.done") : t("connectMac.cancelled")}
            </CardTitle>
            <CardDescription>
              {connected
                ? t("connectMac.doneDescription")
                : t("connectMac.cancelledDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </CenteredScreen>
    );
  }

  const data = request.data;
  const otherOrganizations = me.organizations.filter(
    (org) => org.access === "member" && org.id !== data.organization.id,
  );

  return (
    <CenteredScreen>
      <Card>
        <CardHeader>
          <CardTitle>{t("connectMac.title")}</CardTitle>
          <CardDescription className="text-foreground">
            {t("connectMac.description", {
              organization: data.organization.name,
              email: data.user.email,
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {otherOrganizations.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("connectMac.otherOrganization")}
            </p>
          ) : null}
          {decide.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {decide.error.message}
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <Button
            className="w-full"
            size="lg"
            onClick={() => decide.mutate("approve")}
            disabled={decide.isPending}
          >
            {decide.isPending && decide.variables === "approve"
              ? t("connectMac.connecting")
              : t("connectMac.connect")}
          </Button>
          <Button
            className="w-full"
            variant="ghost"
            onClick={() => decide.mutate("deny")}
            disabled={decide.isPending}
          >
            {t("connectMac.cancel")}
          </Button>
          <Button
            variant="linkSecondary"
            size="sm"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            {t("connectMac.otherAccount", { email: me.user.email })}
          </Button>
        </CardFooter>
      </Card>
    </CenteredScreen>
  );
};
